import React, { useEffect, useState, useCallback, useRef } from "react";
import api, { apiErr } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { useParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { FileCheck, ListChecks, Clock, ShieldAlert, Archive, Upload, Check, X, RefreshCw, Eye, BadgeCheck, Gavel, Flag, IdCard, FileDown } from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLE = {
  "Validé": "bg-emerald-100 text-emerald-700",
  "Finalisé": "bg-emerald-600 text-white",
  "En attente validation Direction": "bg-amber-100 text-amber-700",
  "Refusé": "bg-red-100 text-red-700",
  "À remplacer": "bg-orange-100 text-orange-700",
  "À vérifier": "bg-amber-100 text-amber-700",
  "Déposé": "bg-amber-100 text-amber-700",
  "Manquant": "bg-rose-50 text-rose-600",
  "Attendu": "bg-slate-100 text-slate-500",
};

function ChecklistItem({ item, canManage, onDeposit, onView, onReason, onValidate }) {
  const { t } = useI18n();
  const inputRef = useRef(null);
  const has = !!item.document_id;
  return (
    <Card className="border-slate-200 p-3" data-testid="checklist-item">
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-slate-800">{item.label}</span>
            <Badge className={`border-0 text-xs ${item.obligatoire ? "bg-sky-100 text-sky-700" : "bg-slate-100 text-slate-500"}`}>{item.obligatoire ? t("required") : t("optional")}</Badge>
            {item.sensible && <Badge className="bg-red-100 text-red-700 border-0 text-xs" data-testid="sensitive-badge">{t("sensitive")}</Badge>}
            <Badge className={`border-0 text-xs ${STATUS_STYLE[item.status] || "bg-slate-100 text-slate-400"}`} data-testid={`doc-status-${item.code}`}>{item.status}</Badge>
          </div>
          {has && (
            <div className="mt-1 text-xs text-slate-500 space-y-0.5">
              <div>{item.original_filename}{item.watermarked ? " · 🔒" : ""}</div>
              {item.deposited_by_name && <div>{t("depositedByLabel")}: {item.deposited_by_name} · {(item.deposited_at || "").slice(0, 16).replace("T", " ")}</div>}
              {item.verified_by_name && <div>{t("verifiedByLabel")}: {item.verified_by_name} · {(item.verified_at || "").slice(0, 16).replace("T", " ")}</div>}
              {item.rejected_reason && <div className="text-red-600" data-testid={`doc-reason-${item.code}`}>{t("rejectReasonLabel")}: {item.rejected_reason}</div>}
            </div>
          )}
          <div className="mt-1 text-[11px] text-slate-400">{t("formatsAllowed")}: {(item.formats || []).join(", ")}</div>
        </div>
        <div className="flex items-center gap-1 flex-wrap justify-end">
          {has && (item.can_view_file !== false) && (
            <Button size="sm" variant="ghost" className="gap-1 h-8" data-testid={`view-doc-${item.code}`} onClick={() => onView(item)}><Eye className="h-4 w-4" />{t("viewDoc")}</Button>
          )}
          {canManage && (
            <>
              <input ref={inputRef} type="file" className="hidden" data-testid={`deposit-input-${item.code}`} onChange={(e) => { if (e.target.files?.[0]) onDeposit(item, e.target.files[0]); e.target.value = ""; }} />
              <Button size="sm" variant="outline" className="gap-1 h-8" data-testid={`deposit-doc-${item.code}`} onClick={() => inputRef.current?.click()}><Upload className="h-4 w-4" />{has ? t("replaceDoc") : t("deposit")}</Button>
              {has && item.status !== "Validé" && (
                <Button size="sm" variant="outline" className="gap-1 h-8 text-emerald-700 border-emerald-200" data-testid={`validate-doc-${item.code}`} onClick={() => onValidate(item)}><Check className="h-4 w-4" />{t("validateDoc")}</Button>
              )}
              {has && item.status !== "Refusé" && (
                <Button size="sm" variant="outline" className="gap-1 h-8 text-red-700 border-red-200" data-testid={`reject-doc-${item.code}`} onClick={() => onReason(item, "reject")}><X className="h-4 w-4" />{t("rejectDoc")}</Button>
              )}
              {has && (
                <Button size="sm" variant="ghost" className="gap-1 h-8 text-orange-700" data-testid={`request-replace-${item.code}`} onClick={() => onReason(item, "request-replacement")}><RefreshCw className="h-4 w-4" />{t("requestReplacementDoc")}</Button>
              )}
            </>
          )}
        </div>
      </div>
    </Card>
  );
}

export default function AdmissionDetail() {
  const { t } = useI18n();
  const { hasRole } = useAuth();
  const { id } = useParams();
  const canManage = hasRole("super_admin", "direction", "administration");
  const [d, setD] = useState(null);
  const [groups, setGroups] = useState([]);
  const [reason, setReason] = useState({ open: false, item: null, action: null, text: "", mode: "doc" });
  const [finalizeDlg, setFinalizeDlg] = useState({ open: false, group_id: "", student_name: "", force: false });
  const load = useCallback(() => {
    api.get(`/admissions/${id}`).then((r) => setD(r.data)).catch((e) => toast.error(apiErr(e.response?.data?.detail)));
  }, [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/school/groups").then((r) => setGroups(r.data.items || r.data || [])).catch(() => {}); }, []);

  const archive = async () => { if (!window.confirm(t("archiveConfirm"))) return; try { await api.patch(`/admissions/${id}/archive`); toast.success(t("save")); load(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } };

  const deposit = async (item, file) => {
    const fd = new FormData(); fd.append("file", file); fd.append("type_code", item.code);
    try { await api.post(`/admissions/${id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success(t("save")); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const validate = async (item) => { try { await api.patch(`/admissions/${id}/documents/${item.document_id}/validate`); toast.success(t("save")); load(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } };
  const view = async (item) => { try { const r = await api.get(`/admissions/${id}/documents/${item.document_id}/file`, { responseType: "blob" }); window.open(URL.createObjectURL(r.data), "_blank"); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } };

  const submitReason = async () => {
    if (!reason.text.trim()) { toast.error(t("reasonRequired")); return; }
    try {
      if (reason.mode === "direction") {
        await api.post(`/admissions/${id}/direction-refuse`, { reason: reason.text.trim() });
      } else {
        await api.patch(`/admissions/${id}/documents/${reason.item.document_id}/${reason.action}`, { reason: reason.text.trim() });
      }
      toast.success(t("save")); setReason({ open: false, item: null, action: null, text: "", mode: "doc" }); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const doAction = async (path, body) => { try { await api.post(`/admissions/${id}/${path}`, body || {}); toast.success(t("save")); load(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } };
  const adminValidate = () => doAction("admin-validate");
  const directionValidate = () => doAction("direction-validate");
  const finalize = async () => {
    try {
      await api.post(`/admissions/${id}/finalize`, { group_id: finalizeDlg.group_id || null, student_name: finalizeDlg.student_name || null, force: finalizeDlg.force });
      toast.success(t("save")); setFinalizeDlg({ open: false, group_id: "", student_name: "", force: false }); load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409) { if (window.confirm(detail)) { try { await api.post(`/admissions/${id}/finalize`, { group_id: finalizeDlg.group_id || null, student_name: finalizeDlg.student_name || null, force: true }); toast.success(t("save")); setFinalizeDlg({ open: false, group_id: "", student_name: "", force: false }); load(); return; } catch (e2) { toast.error(apiErr(e2.response?.data?.detail)); } } }
      else toast.error(apiErr(detail));
    }
  };
  const generateCard = async (extra) => {
    try {
      const r = await api.post(`/admissions/${id}/generate-card`, extra || {});
      if (r.data?.already_exists) { if (window.confirm(t("cardExists"))) return generateCard({ ...extra, replace: true }); return; }
      toast.success(t("cardGenerated")); load();
    } catch (e) {
      if (e.response?.status === 409 && e.response?.data?.detail === "no_validated_photo") {
        if (window.confirm(t("noPhotoWarning"))) return generateCard({ ...extra, confirm_placeholder: true });
      } else toast.error(apiErr(e.response?.data?.detail));
    }
  };
  const summaryPdf = async () => { try { const r = await api.get(`/admissions/${id}/summary.pdf`, { responseType: "blob" }); window.open(URL.createObjectURL(r.data), "_blank"); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } };

  if (!d) return <div className="text-slate-400">{t("loading")}</div>;
  const s = d.doc_summary || { required_total: 0, validated: 0, missing: 0, refused: 0, pct_complete: 100 };
  const canDirection = d.can_direction;
  return (
    <div data-testid="admission-detail">
      <div className="mb-6 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <FileCheck className="h-6 w-6 text-primary" />
          <div>
            <h1 className="text-2xl font-display font-bold text-slate-900">{d.file_number}</h1>
            <p className="text-sm text-slate-500">{t(d.type)} · {d.family_name || "—"} · {d.student_name || "—"} · {d.level_name || "—"} · {t(`sit_${d.family_situation}`)}</p>
          </div>
          <Badge className={`border-0 ${STATUS_STYLE[d.status] || "bg-slate-100 text-slate-600"}`} data-testid="dossier-status">{d.status}</Badge>
        </div>
        {canManage && d.status !== "Archivé" && <Button size="sm" variant="outline" className="gap-1" data-testid="admission-archive-btn" onClick={archive}><Archive className="h-4 w-4" />{t("archive")}</Button>}
      </div>

      {d.requires_direction_validation && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-700" data-testid="needs-direction-alert">
          <ShieldAlert className="h-4 w-4" />{t("needsDirection")} — {d.direction_validation_reason}
        </div>
      )}

      {/* Barre d'actions workflow */}
      {canManage && (
        <div className="mb-4 flex items-center gap-2 flex-wrap" data-testid="admission-actions">
          {d.status === "Complet" && !d.admin_validated_at && (
            <Button size="sm" className="gap-1 bg-sky-600 hover:bg-sky-700" data-testid="admin-validate-btn" onClick={adminValidate}><BadgeCheck className="h-4 w-4" />{t("adminValidate")}</Button>
          )}
          {d.status === "En attente validation Direction" && canDirection && (
            <>
              <Button size="sm" className="gap-1 bg-emerald-600 hover:bg-emerald-700" data-testid="direction-validate-btn" onClick={directionValidate}><Gavel className="h-4 w-4" />{t("directionValidate")}</Button>
              <Button size="sm" variant="outline" className="gap-1 text-red-700 border-red-200" data-testid="direction-refuse-btn" onClick={() => setReason({ open: true, mode: "direction", text: "", item: null, action: null })}><Flag className="h-4 w-4" />{t("directionRefuse")}</Button>
            </>
          )}
          {d.status === "Validé" && (
            <Button size="sm" className="gap-1 bg-emerald-600 hover:bg-emerald-700" data-testid="finalize-btn" onClick={() => setFinalizeDlg({ open: true, group_id: "", student_name: d.student_name || "", force: false })}><Check className="h-4 w-4" />{t("finalize")}</Button>
          )}
          {d.status === "Finalisé" && (
            <Button size="sm" className="gap-1 bg-indigo-600 hover:bg-indigo-700" data-testid="generate-card-btn" onClick={() => generateCard({})}><IdCard className="h-4 w-4" />{t("generateCard")}</Button>
          )}
          <Button size="sm" variant="outline" className="gap-1" data-testid="summary-pdf-btn" onClick={summaryPdf}><FileDown className="h-4 w-4" />{t("summaryPdf")}</Button>
        </div>
      )}

      <Card className="mb-4 border-slate-200 p-4" data-testid="doc-summary">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="text-sm text-slate-700">
            <span className="font-semibold text-slate-900">{s.validated}/{s.required_total}</span> {t("docsValidated")}
            {s.missing > 0 && <span className="ml-3 text-rose-600">· {s.missing} {t("docsMissing")}</span>}
            {s.refused > 0 && <span className="ml-3 text-red-600">· {s.refused} {t("docsRefused")}</span>}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-400">{t("docSummary")}</span>
            <Badge className={`border-0 ${STATUS_STYLE[d.status] || "bg-slate-100 text-slate-600"}`}>{d.status}</Badge>
          </div>
        </div>
        <div className="mt-2 h-2 w-full rounded-full bg-slate-100 overflow-hidden">
          <div className="h-full bg-emerald-500 transition-all" style={{ width: `${s.pct_complete}%` }} data-testid="doc-progress" />
        </div>
      </Card>

      <Tabs defaultValue="checklist">
        <TabsList>
          <TabsTrigger value="checklist" data-testid="tab-checklist"><ListChecks className="h-4 w-4 mr-2" />{t("checklist")}</TabsTrigger>
          <TabsTrigger value="timeline" data-testid="tab-atimeline"><Clock className="h-4 w-4 mr-2" />{t("timeline")}</TabsTrigger>
        </TabsList>
        <TabsContent value="checklist">
          <p className="text-xs text-slate-400 mb-2">{d.required_count} {t("requiredDocs")}</p>
          <div className="space-y-2" data-testid="admission-checklist">
            {d.checklist.map((c) => (
              <ChecklistItem key={c.code} item={c} canManage={canManage} onDeposit={deposit} onView={view} onValidate={validate} onReason={(item, action) => setReason({ open: true, item, action, text: "", mode: "doc" })} />
            ))}
          </div>
        </TabsContent>
        <TabsContent value="timeline">
          <div className="space-y-2" data-testid="admission-timeline">
            {d.events.length === 0 ? <p className="text-sm text-slate-400">{t("noData")}</p> :
              d.events.map((e, i) => (
                <div key={i} className="flex gap-3 items-start rounded-lg border border-slate-100 px-3 py-2">
                  <div className="h-2 w-2 rounded-full bg-primary mt-2" />
                  <div><div className="text-sm font-medium text-slate-800">{e.title}</div>
                    <div className="text-xs text-slate-500">{(e.created_at || "").slice(0, 16).replace("T", " ")}{e.detail ? ` · ${e.detail}` : ""}</div></div>
                </div>))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Dialog motif (refus pièce / demande remplacement / refus Direction) */}
      <Dialog open={reason.open} onOpenChange={(o) => setReason((r) => ({ ...r, open: o }))}>
        <DialogContent data-testid="reason-dialog">
          <DialogHeader>
            <DialogTitle>{reason.mode === "direction" ? t("directionRefuse") : (reason.action === "reject" ? t("rejectDoc") : t("requestReplacementDoc"))}</DialogTitle>
            <DialogDescription>{reason.mode === "direction" ? d.file_number : reason.item?.label}</DialogDescription>
          </DialogHeader>
          <Textarea data-testid="reason-input" placeholder={t("rejectReasonLabel")} value={reason.text} onChange={(e) => setReason((r) => ({ ...r, text: e.target.value }))} />
          <DialogFooter>
            <Button data-testid="reason-confirm-btn" onClick={submitReason}>{t("confirmReason")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog finalisation */}
      <Dialog open={finalizeDlg.open} onOpenChange={(o) => setFinalizeDlg((f) => ({ ...f, open: o }))}>
        <DialogContent data-testid="finalize-dialog">
          <DialogHeader>
            <DialogTitle>{t("finalize")}</DialogTitle>
            <DialogDescription>{d.file_number} — {d.family_name}</DialogDescription>
          </DialogHeader>
          {!d.student_id && (
            <div className="space-y-1">
              <label className="text-xs text-slate-500">{t("studentName")}</label>
              <Input data-testid="finalize-student-name" value={finalizeDlg.student_name} onChange={(e) => setFinalizeDlg((f) => ({ ...f, student_name: e.target.value }))} />
            </div>
          )}
          <div className="space-y-1">
            <label className="text-xs text-slate-500">{t("selectGroup")}</label>
            <Select value={finalizeDlg.group_id} onValueChange={(v) => setFinalizeDlg((f) => ({ ...f, group_id: v }))}>
              <SelectTrigger data-testid="finalize-group-select"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                {groups.map((g) => (<SelectItem key={g.id} value={g.id}>{g.name}{g.capacity_status ? ` · ${g.capacity_status}` : ""}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button data-testid="finalize-confirm-btn" onClick={finalize}>{t("finalize")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
